import { useCallback, useEffect } from 'react';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import {
	useAdjustmentsEnabled,
	useUiModificationsEnabledStatus,
} from '@atlassian/ui-modifications-core/controllers';
import { useFormContext } from '@atlassian/jira-issue-create-form-context/src/index.tsx';
import { useMinimizableModal } from '@atlassian/jira-minimizable-modal/src/controllers/use-minimizable-modal/index.tsx';
import { useTriggerIssueCreateModal } from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal/src/main.tsx';
import { useMinimizableModalWrapperProps } from '../../../../controllers/minimizable-modal-wrapper-props/main.tsx';
import messages from './messages.tsx';

export const useViewChangerDisabler = () => {
	const { formatMessage } = useIntl();
	// Global opt-out of minimizable modal
	const [{ disableMinimizableModal }] = useTriggerIssueCreateModal();
	const {
		isNestedModal,
		shouldDisableViewChangerButtons,
		setShouldDisableViewChangerButtons,
		setView,
		view,
	} = useMinimizableModal();
	const [
		{
			ecosystem: {
				forge: { hasFinishedLoading },
			},
		},
	] = useFormContext();
	const adjustmentsAreEnabled = useAdjustmentsEnabled();
	const uiModificationsStatus = useUiModificationsEnabledStatus(hasFinishedLoading);

	const { setShouldRestore } = useMinimizableModalWrapperProps();

	const disableViewChangerButtonsHandler = useCallback(
		(disableViewChangerButtons: boolean, customDisabledViewChangerButtonsMessage?: string) => {
			setShouldDisableViewChangerButtons(
				disableViewChangerButtons,
				customDisabledViewChangerButtonsMessage,
			);
			setShouldRestore?.(!disableViewChangerButtons);
		},
		[setShouldDisableViewChangerButtons, setShouldRestore],
	);

	useEffect(() => {
		const changerButtonsDisabledForgeFieldsIssueTermRefreshGalaxia = expVal(
			'project-terminology-refresh',
			'isEnabled',
			false,
		)
			? messages.changerButtonsDisabledForgeFieldsIssueTermRefreshGalaxia
			: messages.changerButtonsDisabledForgeFieldsIssueTermRefresh;
		if (disableMinimizableModal || isNestedModal) {
			disableViewChangerButtonsHandler(true);
		} else if (uiModificationsStatus === 'enabled' || adjustmentsAreEnabled) {
			disableViewChangerButtonsHandler(
				true,
				formatMessage(
					expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
						? changerButtonsDisabledForgeFieldsIssueTermRefreshGalaxia
						: messages.changerButtonsDisabledForgeFields,
				),
			);
		} else {
			disableViewChangerButtonsHandler(false);
		}
	}, [
		adjustmentsAreEnabled,
		disableMinimizableModal,
		disableViewChangerButtonsHandler,
		formatMessage,
		isNestedModal,
		setShouldDisableViewChangerButtons,
		uiModificationsStatus,
	]);

	useEffect(() => {
		/*
            force setView to modal view is disabled for the full screen since there will be no transitions allowed from the view.
            this will help us enable the UIM flow in the full screen view as well.
        */
		if (view === 'fullscreen') {
			return;
		}
		if (shouldDisableViewChangerButtons) {
			setView('modal', true);
		}
	}, [setView, view, shouldDisableViewChangerButtons]);
};
